// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { URL } from "../../service/Service";
import ErrorMessage from "../../components/ErrorMessage";
import SuccessMessage from "../../components/SuccessMessage";
import {
  BellIcon,
  CheckCircleIcon,
  ClockIcon,
  PencilSquareIcon,
  UserPlusIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

const getNotificationId = (notification) => {
  return (
      notification?.notificationID ||
      notification?.notificationId ||
      notification?.id ||
      notification?.notification?.notificationID ||
      null
  );
};

const Notifications = ({ userId }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentTab, setCurrentTab] = useState("PENDING");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [cidFilter, setCidFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("NEWEST");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkReject, setShowBulkReject] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const pageSize = 5;

  const jwtToken = sessionStorage.getItem("jwt");

  const statusGroups = useMemo(
      () => ({
        PENDING: ["PENDING", "UPDATE_PENDING"],
        APPROVED: ["APPROVED", "UPDATE_APPROVED"],
        REJECTED: ["REJECTED", "UPDATE_REJECTED"],
      }),
      []
  );

  const notifySidebarRefresh = () => {
    window.dispatchEvent(new Event("notifications:changed"));
    window.dispatchEvent(new Event("notifications:refresh"));

    window.setTimeout(() => {
      window.dispatchEvent(new Event("notifications:changed"));
      window.dispatchEvent(new Event("notifications:refresh"));
    }, 400);

    window.setTimeout(() => {
      window.dispatchEvent(new Event("notifications:changed"));
      window.dispatchEvent(new Event("notifications:refresh"));
    }, 1200);
  };

  const markNotificationsAsRead = useCallback(async (items) => {
    if (!jwtToken || !Array.isArray(items) || items.length === 0) {
      notifySidebarRefresh();
      return;
    }

    const notificationIds = items
        .map((notification) => getNotificationId(notification))
        .filter(Boolean);

    if (notificationIds.length === 0) {
      notifySidebarRefresh();
      return;
    }

    try {
      await Promise.all(
          notificationIds.map((notificationId) =>
              axios.put(
                  `${URL}/api/user/notifications/${notificationId}/read`,
                  {},
                  {
                    headers: {
                      Authorization: `Bearer ${jwtToken}`,
                    },
                  }
              )
          )
      );

      notifySidebarRefresh();
    } catch (error) {
      console.error("Erro ao marcar notificações como lidas:", error);
    }
  }, [jwtToken]);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(`${URL}/api/user/notifications`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      const data = Array.isArray(response.data) ? response.data : [];

      setNotifications(data);

      // IMPORTANTE:
      // Ao entrar na página de notificações do admin,
      // marca as notificações como lidas para a sidebar passar para 0.
      await markNotificationsAsRead(data);
    } catch (error) {
      console.error("Erro ao carregar notificações:", error);
      setError("Falha ao carregar as notificações.");
    } finally {
      setLoading(false);
    }
  }, [jwtToken, markNotificationsAsRead]);

  useEffect(() => {
    if (!jwtToken) {
      setError("Sessão expirada. Faça login novamente.");
      setLoading(false);
      return;
    }

    fetchNotifications();
  }, [jwtToken, fetchNotifications]);

  const getStatusToFilter = (notification) => {
    if (notification?.metadata?.customerStatusUpdate) {
      return notification.metadata.customerStatusUpdate;
    }

    return notification?.metadata?.customerStatus;
  };

  const tabNotifications = useMemo(() => {
    return notifications.filter((notification) =>
        statusGroups[currentTab].includes(getStatusToFilter(notification))
    );
  }, [notifications, currentTab, statusGroups]);

  const filteredNotifications = useMemo(() => {
    const partnerQuery = partnerFilter.trim().toLowerCase();
    const cidQuery = cidFilter.trim().toLowerCase();

    return [...tabNotifications]
        .filter((notification) => {
          const metadata = notification?.metadata || {};
          const partnerMatches = !partnerQuery || String(metadata.partnerName || "").toLowerCase().includes(partnerQuery);
          const cidMatches = !cidQuery || String(metadata.customerID || "").toLowerCase().includes(cidQuery);
          const isEdition = Boolean(metadata.customerStatusUpdate);
          const typeMatches = typeFilter === "ALL" || (typeFilter === "UPDATE" ? isEdition : !isEdition);
          return partnerMatches && cidMatches && typeMatches;
        })
        .sort((left, right) => {
          const leftDate = new Date(String(left?.createAt || "").replace(" ", "T")).getTime() || 0;
          const rightDate = new Date(String(right?.createAt || "").replace(" ", "T")).getTime() || 0;
          return sortOrder === "NEWEST" ? rightDate - leftDate : leftDate - rightDate;
        });
  }, [tabNotifications, partnerFilter, cidFilter, typeFilter, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filteredNotifications.length / pageSize));
  const paginatedNotifications = filteredNotifications.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [currentTab, partnerFilter, cidFilter, typeFilter, sortOrder]);

  const updateNotificationLocally = (notificationID, field, value) => {
    setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
            getNotificationId(notification) === notificationID
                ? {
                  ...notification,
                  metadata: {
                    ...notification.metadata,
                    [field]: value,
                  },
                }
                : notification
        )
    );
  };

  const handleApproval = async (
      partnerID,
      notificationID,
      customerID,
      approvalStatus,
      reason = ""
  ) => {
    const requestBody = {
      customerStatus: approvalStatus ? "APPROVED" : "REJECTED",
      rejectionReason: reason,
    };

    try {
      setError("");
      setSuccess("");
      setProcessingId(notificationID);

      await axios.post(
          `${URL}/api/partners/${partnerID}/notifications/${notificationID}/customers/${customerID}/approval`,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              "Content-Type": "application/json",
            },
          }
      );

      updateNotificationLocally(
          notificationID,
          "customerStatus",
          approvalStatus ? "APPROVED" : "REJECTED"
      );

      setSuccess(
          `Pedido ${approvalStatus ? "aprovado" : "rejeitado"} com sucesso.`
      );

      await markNotificationsAsRead([
        {
          notificationID,
        },
      ]);

      window.setTimeout(() => {
        fetchNotifications();
      }, 300);
    } catch (error) {
      console.error("Erro ao processar pedido:", error);
      setError("Erro ao processar o pedido.");
    } finally {
      setProcessingId(null);
    }
  };

  const handleEditionApproval = async (
      partnerID,
      notificationID,
      customerID,
      approvalStatus,
      reason = ""
  ) => {
    const requestBody = {
      customerStatusUpdate: approvalStatus
          ? "UPDATE_APPROVED"
          : "UPDATE_REJECTED",
      rejectionReason: reason,
    };

    try {
      setError("");
      setSuccess("");
      setProcessingId(notificationID);

      await axios.post(
          `${URL}/api/partners/${partnerID}/notifications/${notificationID}/customers/${customerID}/editionApproval`,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              "Content-Type": "application/json",
            },
          }
      );

      updateNotificationLocally(
          notificationID,
          "customerStatusUpdate",
          approvalStatus ? "UPDATE_APPROVED" : "UPDATE_REJECTED"
      );

      setSuccess(
          `Edição ${approvalStatus ? "aprovada" : "rejeitada"} com sucesso.`
      );

      await markNotificationsAsRead([
        {
          notificationID,
        },
      ]);

      window.setTimeout(() => {
        fetchNotifications();
      }, 300);
    } catch (error) {
      console.error("Erro ao processar edição:", error);
      setError("Erro ao processar a edição.");
    } finally {
      setProcessingId(null);
    }
  };

  const toggleReviewedSelection = (notificationID) => {
    setSelectedIds((current) =>
        current.includes(notificationID)
            ? current.filter((id) => id !== notificationID)
            : [...current, notificationID]
    );
  };

  const handleBulkReject = async () => {
    const reason = rejectionReason.trim();
    if (!reason) {
      setError("Indique o motivo da rejeição antes de continuar.");
      return;
    }

    const selected = notifications.filter((notification) => selectedIds.includes(getNotificationId(notification)));
    if (selected.length === 0) return;

    try {
      setBulkProcessing(true);
      setError("");

      for (const notification of selected) {
        const metadata = notification?.metadata || {};
        const notificationID = getNotificationId(notification);
        if (metadata.customerStatusUpdate === "UPDATE_PENDING") {
          await handleEditionApproval(metadata.partnerID, notificationID, metadata.customerID, false, reason);
        } else if (metadata.customerStatus === "PENDING") {
          await handleApproval(metadata.partnerID, notificationID, metadata.customerID, false, reason);
        }
      }

      setSelectedIds([]);
      setRejectionReason("");
      setShowBulkReject(false);
      setSuccess(`${selected.length} pedido(s) rejeitado(s) com sucesso.`);
      await fetchNotifications();
    } finally {
      setBulkProcessing(false);
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "-";

    const parts = dateTimeString.split(" ");

    if (parts.length < 2) {
      return dateTimeString;
    }

    const [date, time] = parts;
    return `${time} ${date}`;
  };

  const getTabLabel = (status) => {
    if (status === "PENDING") return "Pendentes";
    if (status === "APPROVED") return "Aprovadas";
    return "Rejeitadas";
  };

  const getNotificationText = (notification) => {
    const metadata = notification?.metadata || {};
    const customerName = metadata.customerName || "cliente";

    if (metadata.customerStatus === "PENDING") {
      return (
          <>
            A aguardar aprovação de pedido de criação de novo cliente{" "}
            <span className="font-semibold">{customerName}</span>
          </>
      );
    }

    if (metadata.customerStatusUpdate === "UPDATE_PENDING") {
      return (
          <>
            A aguardar aprovação de atualização do cliente{" "}
            <span className="font-semibold">{customerName}</span>
          </>
      );
    }

    if (metadata.customerStatus === "APPROVED") {
      return (
          <>
            Pedido de criação de cliente{" "}
            <span className="font-semibold">{customerName}</span> foi aprovado.
          </>
      );
    }

    if (metadata.customerStatusUpdate === "UPDATE_APPROVED") {
      return (
          <>
            Atualização do cliente{" "}
            <span className="font-semibold">{customerName}</span> foi aprovada.
          </>
      );
    }

    if (metadata.customerStatus === "REJECTED") {
      return (
          <>
            Pedido de criação de cliente{" "}
            <span className="font-semibold">{customerName}</span> foi rejeitado.
          </>
      );
    }

    if (metadata.customerStatusUpdate === "UPDATE_REJECTED") {
      return (
          <>
            Atualização do cliente{" "}
            <span className="font-semibold">{customerName}</span> foi rejeitada.
          </>
      );
    }

    return "Notificação sem estado definido.";
  };

  const hasPendingAction = (notification) => {
    return (
        notification?.metadata?.customerStatus === "PENDING" ||
        notification?.metadata?.customerStatusUpdate === "UPDATE_PENDING"
    );
  };

  const getVisualStatus = (notification) => {
    const status = getStatusToFilter(notification);
    const isPending = status === "PENDING" || status === "UPDATE_PENDING";
    const isApproved = status === "APPROVED" || status === "UPDATE_APPROVED";
    const isUpdate = String(status || "").startsWith("UPDATE_");

    return {
      isPending,
      isApproved,
      isUpdate,
      label: isPending ? "Pendente" : isApproved ? "Aprovado" : "Rejeitado",
      barClass: isPending ? "bg-amber-400" : isApproved ? "bg-green-500" : "bg-red-500",
      iconClass: isPending ? "bg-amber-50 text-amber-700" : isApproved ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700",
      badgeClass: isPending ? "bg-amber-50 text-amber-700 ring-amber-200" : isApproved ? "bg-green-50 text-green-700 ring-green-200" : "bg-red-50 text-red-700 ring-red-200",
    };
  };

  const getEditionChanges = (metadata) => {
    const fields = [
      { label: "Nome", previous: "previousName", proposed: "customerName" },
      { label: "NIF", previous: "previousNif", proposed: "nif" },
      { label: "Website", previous: "previousWebsite", proposed: "website" },
      { label: "Notas", previous: "previousNotes", proposed: "notes" },
    ];

    return fields
        .map((field) => ({
          label: field.label,
          previous: String(metadata?.[field.previous] ?? "").trim(),
          proposed: String(metadata?.[field.proposed] ?? "").trim(),
        }))
        .filter((field) => field.previous !== field.proposed);
  };

  if (loading) {
    return (
        <div className="flex h-full min-h-[300px] items-center justify-center">
          <p className="text-lg text-gray-500">A carregar...</p>
        </div>
    );
  }

  return (
      <main className="relative mx-auto max-w-6xl px-5 pb-12 pt-10 sm:px-8 sm:pt-12">
        <ErrorMessage message={error} />
        <SuccessMessage message={success} />

        <header className="mb-7 flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-cl ring-1 ring-green-100">
            <BellIcon className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-gray-950">Notificações</h1>
            <p className="mt-1 text-sm text-gray-500">
              Consulte e trate pedidos de criação ou atualização de clientes feitos pelos parceiros.
            </p>
          </div>
        </header>

        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-2" aria-label="Estado das notificações">
            {["PENDING", "APPROVED", "REJECTED"].map((status) => (
                <button
                    key={status}
                    type="button"
                    className={`mb-[-1px] inline-flex items-center gap-2 rounded-t-xl border-b-2 px-4 py-3 text-sm font-bold transition ${
                        currentTab === status
                            ? status === "PENDING"
                                ? "border-amber-400 bg-amber-50 text-amber-800"
                                : status === "APPROVED"
                                    ? "border-green-cl bg-green-50 text-green-800"
                                    : "border-red-500 bg-red-50 text-red-700"
                            : "border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                    }`}
                    onClick={() => setCurrentTab(status)}
                >
                  {status === "PENDING" ? <ClockIcon className="h-4 w-4" /> : status === "APPROVED" ? <CheckCircleIcon className="h-4 w-4" /> : <XCircleIcon className="h-4 w-4" />}
                  {getTabLabel(status)}
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs ring-1 ring-gray-200">
                    {notifications.filter((item) => statusGroups[status].includes(getStatusToFilter(item))).length}
                  </span>
                </button>
            ))}
          </nav>
        </div>

        {currentTab === "PENDING" && (
            <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <input
                    value={partnerFilter}
                    onChange={(event) => setPartnerFilter(event.target.value)}
                    placeholder="Filtrar por parceiro"
                    className="min-h-[42px] rounded-xl border border-gray-300 px-3 text-sm outline-none focus:border-green-cl focus:ring-2 focus:ring-green-100"
                />
                <input
                    value={cidFilter}
                    onChange={(event) => setCidFilter(event.target.value)}
                    placeholder="Filtrar por CID"
                    className="min-h-[42px] rounded-xl border border-gray-300 px-3 text-sm outline-none focus:border-green-cl focus:ring-2 focus:ring-green-100"
                />
                <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} className="min-h-[42px] rounded-xl border border-gray-300 bg-white px-3 text-sm">
                  <option value="ALL">Todos os pedidos</option>
                  <option value="CREATE">Criação de cliente</option>
                  <option value="UPDATE">Atualização de cliente</option>
                </select>
                <select value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} className="min-h-[42px] rounded-xl border border-gray-300 bg-white px-3 text-sm">
                  <option value="NEWEST">Mais recentes primeiro</option>
                  <option value="OLDEST">Mais antigos primeiro</option>
                </select>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
                <p className="text-sm text-gray-600">
                  <strong>{selectedIds.length}</strong> pedido(s) revisto(s) e selecionado(s)
                </p>
                <button
                    type="button"
                    disabled={selectedIds.length === 0}
                    onClick={() => setShowBulkReject(true)}
                    className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-bold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Rejeitar selecionados
                </button>
              </div>

              {showBulkReject && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4">
                    <h3 className="font-bold text-red-900">Confirmar rejeição de {selectedIds.length} pedido(s)</h3>
                    <p className="mt-1 text-sm text-red-700">O motivo é obrigatório e será guardado na notificação enviada ao parceiro.</p>
                    <textarea
                        value={rejectionReason}
                        onChange={(event) => setRejectionReason(event.target.value)}
                        rows={3}
                        placeholder="Explique o motivo da rejeição..."
                        className="mt-3 w-full rounded-xl border border-red-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-200"
                    />
                    <div className="mt-3 flex justify-end gap-3">
                      <button type="button" onClick={() => setShowBulkReject(false)} className="rounded-xl bg-white px-4 py-2 text-sm font-bold text-gray-700 ring-1 ring-gray-300">Cancelar</button>
                      <button type="button" disabled={bulkProcessing || !rejectionReason.trim()} onClick={handleBulkReject} className="rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
                        {bulkProcessing ? "A rejeitar..." : "Confirmar rejeição"}
                      </button>
                    </div>
                  </div>
              )}
            </div>
        )}

        <div className="space-y-4">
          {filteredNotifications.length > 0 ? (
              paginatedNotifications.map((notification) => {
                const metadata = notification?.metadata || {};
                const notificationID = getNotificationId(notification);
                const isProcessing = processingId === notificationID;
                const isPendingEdition = metadata.customerStatusUpdate === "UPDATE_PENDING";
                const hasVerifiedChangeDetails = metadata.changeDetailsVersion === "1";
                const editionChanges = isPendingEdition
                    ? getEditionChanges(metadata)
                    : [];
                const canApproveEdition = hasVerifiedChangeDetails && editionChanges.length > 0;
                const visualStatus = getVisualStatus(notification);
                const TypeIcon = visualStatus.isUpdate ? PencilSquareIcon : UserPlusIcon;

                return (
                    <div
                        key={notificationID}
                        className={`flex flex-col items-start gap-4 rounded-2xl border border-l-4 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md sm:flex-row sm:p-6 ${
                          visualStatus.isPending
                              ? "border-amber-100 border-l-amber-400"
                              : visualStatus.isApproved
                                  ? "border-green-100 border-l-green-500"
                                  : "border-red-100 border-l-red-500"
                        }`}
                    >
                      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${visualStatus.iconClass}`}>
                        <TypeIcon className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        {currentTab === "PENDING" && (
                            <label className="mb-4 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs font-bold text-gray-700 ring-1 ring-gray-200">
                              <input
                                  type="checkbox"
                                  checked={selectedIds.includes(notificationID)}
                                  onChange={() => toggleReviewedSelection(notificationID)}
                                  className="h-4 w-4 rounded border-gray-300 text-green-cl focus:ring-green-cl"
                              />
                              Revisto — selecionar para rejeição
                            </label>
                        )}
                        <div className="mb-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="font-bold text-gray-950">
                              {visualStatus.isUpdate ? "Atualização de cliente" : "Criação de cliente"}
                            </h2>
                            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${visualStatus.badgeClass}`}>
                              {visualStatus.label}
                            </span>
                          </div>

                          <span className="mt-2 block text-sm text-gray-600">
                            Pedido efetuado por <strong className="text-gray-900">{metadata.partnerName || "parceiro"}</strong>
                          </span>

                          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
                            <span><strong className="text-gray-700">Cliente:</strong> {metadata.customerName || "-"}</span>
                            <span><strong className="text-gray-700">CID:</strong> {metadata.customerID || "-"}</span>
                            <time>{formatDateTime(notification.createAt)}</time>
                          </div>
                        </div>

                        <div>
                    <span className="block text-gray-900">
                      {getNotificationText(notification)}
                    </span>
                        </div>

                        {isPendingEdition && (
                            <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-4">
                              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                <h3 className="text-sm font-bold text-gray-900">Alterações solicitadas</h3>
                                <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-600 ring-1 ring-gray-200">
                                  CID {metadata.customerID || "-"}
                                </span>
                              </div>

                              {canApproveEdition ? (
                                  <div className="space-y-3">
                                    {editionChanges.map((change) => (
                                        <div key={change.label} className="grid gap-2 text-sm sm:grid-cols-[110px_1fr_28px_1fr] sm:items-start">
                                          <span className="font-semibold text-gray-700">{change.label}</span>
                                          <span className="break-words rounded-lg bg-white px-3 py-2 text-gray-500 ring-1 ring-gray-200">
                                            {change.previous || "Sem valor"}
                                          </span>
                                          <span className="hidden pt-2 text-center font-bold text-green-cl sm:block">→</span>
                                          <span className="break-words rounded-lg bg-green-50 px-3 py-2 font-semibold text-gray-900 ring-1 ring-green-200">
                                            {change.proposed || "Sem valor"}
                                          </span>
                                        </div>
                                    ))}
                                  </div>
                              ) : (
                                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800">
                                    Esta notificação antiga não contém detalhes verificáveis. Rejeite-a e peça ao parceiro para submeter novamente a alteração.
                                  </p>
                              )}
                            </div>
                        )}
                      </div>

                      {hasPendingAction(notification) && (
                          <div className="mt-4 flex w-full flex-col space-y-2 sm:mt-0 sm:w-auto">
                            {metadata.customerStatus === "PENDING" && (
                                <>
                                  <button
                                      type="button"
                                      disabled={isProcessing}
                                      className="rounded-md bg-green-500 px-4 py-2 text-white transition hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                                      onClick={() =>
                                          handleApproval(
                                              metadata.partnerID,
                                              notificationID,
                                              metadata.customerID,
                                              true
                                          )
                                      }
                                  >
                                    {isProcessing ? "A processar..." : "Aceitar Pedido"}
                                  </button>

                                  <button
                                      type="button"
                                      disabled={isProcessing}
                                      className="rounded-md bg-red-500 px-4 py-2 text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                                      onClick={() =>
                                          handleApproval(
                                              metadata.partnerID,
                                              notificationID,
                                              metadata.customerID,
                                              false
                                          )
                                      }
                                  >
                                    {isProcessing ? "A processar..." : "Rejeitar Pedido"}
                                  </button>
                                </>
                            )}

                            {metadata.customerStatusUpdate === "UPDATE_PENDING" && (
                                <>
                                  <button
                                      type="button"
                                      disabled={isProcessing || !canApproveEdition}
                                      title={!canApproveEdition ? "Não é possível aprovar sem consultar as alterações." : undefined}
                                      className="rounded-md bg-green-500 px-4 py-2 text-white transition hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                                      onClick={() =>
                                          handleEditionApproval(
                                              metadata.partnerID,
                                              notificationID,
                                              metadata.customerID,
                                              true
                                          )
                                      }
                                  >
                                    {isProcessing ? "A processar..." : "Aceitar Edição"}
                                  </button>

                                  <button
                                      type="button"
                                      disabled={isProcessing}
                                      className="rounded-md bg-red-500 px-4 py-2 text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                                      onClick={() =>
                                          handleEditionApproval(
                                              metadata.partnerID,
                                              notificationID,
                                              metadata.customerID,
                                              false
                                          )
                                      }
                                  >
                                    {isProcessing ? "A processar..." : "Rejeitar Edição"}
                                  </button>
                                </>
                            )}
                          </div>
                      )}
                    </div>
                );
              })
          ) : (
              <div className="flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center">
                <BellIcon className="h-9 w-9 text-gray-300" />
                <p className="mt-3 font-bold text-gray-700">Nenhuma notificação nesta categoria</p>
                <p className="mt-1 text-sm text-gray-500">Os próximos pedidos e decisões aparecerão aqui.</p>
              </div>
          )}
        </div>

        {filteredNotifications.length > 0 && totalPages > 1 && (
            <div className="mt-6 flex items-center justify-end gap-3">
              <button type="button" disabled={page === 1} onClick={() => setPage((value) => value - 1)} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-40">Anterior</button>
              <span className="text-sm font-semibold text-gray-600">{page}/{totalPages}</span>
              <button type="button" disabled={page === totalPages} onClick={() => setPage((value) => value + 1)} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-40">Seguinte</button>
            </div>
        )}
      </main>
  );
};

export default Notifications;
